import { useState } from 'react'
import { Bot, Plus, Trash2, CheckCircle, AlertCircle, XCircle, Loader2 } from 'lucide-react'
import { useTelegramBots, useAddBot, useDeleteBot, type TelegramBot } from '@/api/hooks/useTelegram'
import { toast } from 'sonner'

function statusBadge(bot: TelegramBot) {
  if (!bot.is_active) return <span className="flex items-center gap-1 text-xs text-red-400"><XCircle className="h-3 w-3" /> Inativo</span>
  if (!bot.last_verified_at) return <span className="flex items-center gap-1 text-xs text-yellow-400"><AlertCircle className="h-3 w-3" /> Não verificado</span>
  const hours = (Date.now() - new Date(bot.last_verified_at).getTime()) / 3600000
  if (hours < 24) return <span className="flex items-center gap-1 text-xs text-green-400"><CheckCircle className="h-3 w-3" /> Ativo</span>
  if (hours < 48) return <span className="flex items-center gap-1 text-xs text-yellow-400"><AlertCircle className="h-3 w-3" /> Verificar</span>
  return <span className="flex items-center gap-1 text-xs text-red-400"><XCircle className="h-3 w-3" /> Expirado</span>
}

export function BotsTab({ onSelectBot }: { onSelectBot: (id: string) => void }) {
  const { data: bots, isLoading } = useTelegramBots()
  const addBot = useAddBot()
  const deleteBot = useDeleteBot()
  const [showModal, setShowModal] = useState(false)
  const [token, setToken] = useState('')

  const handleAdd = async () => {
    if (!token.trim()) return
    try {
      const res = await addBot.mutateAsync(token.trim())
      toast.success(res.message || 'Bot adicionado!')
      setToken('')
      setShowModal(false)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const handleDelete = async (bot: TelegramBot) => {
    if (!confirm(`Remover @${bot.bot_username}? Todos os dados serão perdidos.`)) return
    try {
      await deleteBot.mutateAsync(bot.id)
      toast.success('Bot removido')
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{bots?.length || 0} bot(s) conectado(s)</p>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90">
          <Plus className="h-4 w-4" /> Adicionar Bot
        </button>
      </div>

      {(!bots || bots.length === 0) ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center">
          <Bot className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Nenhum bot conectado</p>
          <p className="text-xs text-muted-foreground mt-1">Crie um bot no @BotFather do Telegram e cole o token aqui</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {bots.map((bot) => (
            <div key={bot.id} onClick={() => onSelectBot(bot.id)} className="flex items-center justify-between p-4 rounded-lg border border-border bg-card hover:bg-accent/50 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="font-medium">@{bot.bot_username}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span>{bot.groups_count} grupos</span>
                    <span>{bot.contacts_count} contatos</span>
                    {statusBadge(bot)}
                  </div>
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(bot) }} className="p-2 text-muted-foreground hover:text-red-400 transition-colors" title="Remover bot">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Bot Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Adicionar Bot</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-muted-foreground">Token do BotFather</label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                />
              </div>
              <p className="text-xs text-muted-foreground">Abra @BotFather no Telegram, crie um bot com /newbot e cole o token acima.</p>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm rounded-md border border-border hover:bg-accent">Cancelar</button>
                <button onClick={handleAdd} disabled={addBot.isPending || !token.trim()} className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
                  {addBot.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Conectar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
